import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ALL_ADMIN_ROLES, adminUserAction, fetchManagedUsers, isCurrentUserTeamManager } from "@/lib/adminUsers";
import { db } from "@/lib/billing";
import { useAuth } from "@/hooks/useAuth";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { languageOptions } from "@/hooks/useLanguagePreference";

export default function UserFormPage({ mode }: { mode: "create" | "edit" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const trialMode = useTrialMode();
  const { data: canManage, isLoading: adminLoading } = useQuery({ queryKey: ["can-manage-team", user?.id], queryFn: () => isCurrentUserTeamManager(user?.id), enabled: !!user });
  const { data: managed, isLoading: usersLoading } = useQuery({ queryKey: ["admin-users-managed"], queryFn: fetchManagedUsers, enabled: !!canManage });
  const users = useMemo(() => managed?.users ?? [], [managed?.users]);
  const companies = useMemo(() => managed?.companies ?? [], [managed?.companies]);
  const canManageAll = Boolean(managed?.can_manage_all);
  const teamUsage = managed?.team_usage ?? [];
  const { data: packages = [] } = useQuery({
    queryKey: ["packages-for-user-form"],
    queryFn: async () => {
      const { data, error } = await db.from("packages").select("id, name").eq("is_active", true).order("sort_order");
      if (error) {
        console.warn("Packages table unavailable for user form", error);
        return [];
      }
      return data ?? [];
    },
    enabled: !!canManage,
  });

  const existing = useMemo(() => users.find((item) => item.id === id), [users, id]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    company_id: "",
    company_role: "editor",
    company_name: "",
    address: "",
    country: "",
    preferred_language: "fr",
    avatar_url: "",
    password: "",
    account_status: "invited",
    email_verified: false,
    package_id: "",
    roles: [] as string[],
    note: "",
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (mode === "create" && !canManageAll && companies.length === 1 && !form.company_id) {
      setForm((current) => ({
        ...current,
        company_id: companies[0].id,
        company_name: companies[0].name,
        package_id: "",
        roles: [],
        account_status: "invited",
        email_verified: false,
      }));
    }
  }, [canManageAll, companies, form.company_id, mode]);

  if (!initialized && mode === "edit" && existing) {
    setForm({
      full_name: existing.full_name ?? "",
      email: existing.email ?? "",
      phone_number: existing.phone_number ?? "",
      company_id: existing.company_id ?? "",
      company_role: existing.company_role ?? "editor",
      company_name: existing.company_name ?? "",
      address: existing.address ?? "",
      country: existing.country ?? "",
      preferred_language: existing.preferred_language ?? "fr",
      avatar_url: existing.avatar_url ?? "",
      password: "",
      account_status: existing.account_status ?? "active",
      email_verified: existing.email_verified,
      package_id: existing.package_id ?? "",
      roles: existing.roles,
      note: "",
    });
    setInitialized(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      if (mode === "create") {
        return adminUserAction("create_user", form);
      }
      return adminUserAction("update_user", { user_id: id, ...form });
    },
    onSuccess: async (data: any) => {
      toast.success(data?.message ?? (mode === "create" ? "User created" : "User updated"));
      const targetId = mode === "create" ? data?.user_id : id;
      if (form.note && targetId) await adminUserAction("add_note", { user_id: targetId, note: form.note });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-user-detail", targetId] }),
      ]);
      navigate(targetId ? `/team/users/${targetId}` : "/team");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save user"),
  });

  if (adminLoading || usersLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!canManage) return <Navigate to="/team" replace />;
  if (mode === "edit" && !existing) return <Navigate to="/team" replace />;

  const setRole = (role: string, checked: boolean) => {
    setForm((current) => ({
      ...current,
      roles: checked ? [...current.roles, role] : current.roles.filter((item) => item !== role),
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{mode === "create" ? "Create user" : "Edit user"}</h1>
          <p className="text-xs text-muted-foreground">{canManageAll ? "Manage identity, status, roles, and starting package." : "Invite a company user. Package access comes from the company subscription."}</p>
        </div>
        <Button asChild variant="outline" size="sm"><Link to={id ? `/team/users/${id}` : "/team"}><ArrowLeft className="mr-2 h-4 w-4" />Back</Link></Button>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 p-6">
        <Card>
          <CardHeader><CardTitle className="text-base">User details</CardTitle></CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Field label="Full name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} /></Field>
            <Field label="Company/account">
              <Select value={form.company_id || "none"} onValueChange={(value) => {
                const companyId = value === "none" ? "" : value;
                const company = companies.find((item) => item.id === companyId);
                setForm({ ...form, company_id: companyId, company_name: company?.name ?? form.company_name });
              }} disabled={!canManageAll && companies.length <= 1}>
                <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {canManageAll && <SelectItem value="none">No company</SelectItem>}
                  {companies.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Company role">
              <Select value={form.company_role} onValueChange={(value) => setForm({ ...form, company_role: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(canManageAll ? ["owner", "admin", "editor", "viewer"] : ["admin", "editor", "viewer"]).map((role) => <SelectItem key={role} value={role}>{role === "viewer" ? "member" : role}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Country"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Language">
              <Select value={form.preferred_language} onValueChange={(value) => setForm({ ...form, preferred_language: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {languageOptions.map((o) => (

                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>

                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Avatar URL"><Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} /></Field>
            {mode === "create" && canManageAll && <Field label="Temporary password"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Optional; generated if blank" /></Field>}
            {canManageAll && (
              <>
                <Field label="Account status">
                  <Select value={form.account_status} onValueChange={(value) => setForm({ ...form, account_status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["active", "invited", "suspended", "disabled"].map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="flex items-center justify-between rounded-md border border-border p-3 md:col-span-2">
                  <Label>Email verified</Label>
                  <Switch checked={form.email_verified} onCheckedChange={(checked) => setForm({ ...form, email_verified: checked })} />
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <Field label="Address"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            </div>
            {canManageAll && (
              <Field label={mode === "create" ? "Starting package/subscription" : "Assigned package/subscription"}>
                <Select value={form.package_id || "none"} onValueChange={(value) => setForm({ ...form, package_id: value === "none" ? "" : value })}>
                  <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No package</SelectItem>
                    {packages.map((pkg: any) => <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </CardContent>
        </Card>

        {canManageAll ? (
          <Card>
            <CardHeader><CardTitle className="text-base">Roles</CardTitle></CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
              {ALL_ADMIN_ROLES.map((role) => (
                <label key={role.value} className="flex items-center gap-2 rounded-md border border-border p-3 text-sm">
                  <Checkbox checked={form.roles.includes(role.value)} onCheckedChange={(checked) => setRole(role.value, Boolean(checked))} />
                  {role.label}
                </label>
              ))}
              <p className="text-xs text-muted-foreground md:col-span-3">Platform roles are applied only when the current admin has platform-level permission.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader><CardTitle className="text-base">Team usage</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {teamUsage.map((usage) => (
                <div key={usage.company_id} className="flex items-center justify-between gap-3">
                  <span>{usage.company_name}</span>
                  <span className="text-muted-foreground">{usage.used} / {usage.limit ?? "unlimited"} team members used</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Internal note</CardTitle></CardHeader>
          <CardContent><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional admin note..." /></CardContent>
        </Card>

        <Button onClick={() => save.mutate()} disabled={save.isPending || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
          {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save user
        </Button>
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
